import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, ProgressBar } from "@/components/common";
import { InviteLink, ASSIGN_OPTS } from "@/pages/admin/Admin";

const inp = "w-full h-11 rounded-xl border border-[#EADEC9] bg-white px-3 text-sm";
const PERM_LABEL = { ideas: "Ideas", builder: "Business Builder", courses: "Courses", exercises: "Exercises", checklists: "Checklists", workspace: "My Business", ai_coach: "AI Coach", templates: "Templates", tools: "Tools", coaching: "Coaching" };

export default function AdminUser() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [token, setToken] = useState(null);
  const load = () => api.get(`/admin/users/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!d) return <Loader />;
  const u = d.user;
  const patch = async (body, msg = "Updated") => {
    try { await api.patch(`/admin/users/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const togglePerm = (p) => {
    const has = u.permissions.includes(p);
    patch({ permission_overrides: { ...(u.permission_overrides || {}), [p]: !has } });
  };
  const extend = (days) => {
    const base = u.expiry_date && u.expiry_date > new Date().toISOString().slice(0, 10) ? new Date(u.expiry_date) : new Date();
    base.setDate(base.getDate() + days);
    patch({ expiry_date: base.toISOString().slice(0, 10) }, `Access extended to ${base.toISOString().slice(0, 10)}`);
  };
  const reset = async () => { const { data } = await api.post(`/admin/users/${id}/reset-link`); setToken(data.token); };
  const biz = d.businesses[0];
  return (
    <div className="max-w-4xl" data-testid="admin-user-detail">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-[#5C6B73] mb-4"><ArrowLeft className="h-4 w-4" /> Admin</Link>
      <div className="grid md:grid-cols-2 gap-4">
        <section className="sb-card p-6">
          <div className="sb-eyebrow">User</div>
          <h1 className="text-2xl font-extrabold mt-1" data-testid="admin-user-name">{u.first_name} {u.last_name}</h1>
          <div className="text-sm text-[#5C6B73]">{u.email}</div><div className="text-sm text-[#5C6B73]">{u.phone}</div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <label><span className="text-xs font-semibold text-[#5C6B73]">Plan</span><select value={u.plan} onChange={(e) => patch({ plan: e.target.value }, "Plan changed")} className={inp + " mt-1"} data-testid="admin-user-plan-select">{ASSIGN_OPTS.map(([p, l]) => <option key={p} value={p}>{l}</option>)}</select></label>
            <label><span className="text-xs font-semibold text-[#5C6B73]">Activation date</span><input type="date" defaultValue={u.start_date || ""} onBlur={(e) => e.target.value !== (u.start_date || "") && patch({ start_date: e.target.value })} className={inp + " mt-1"} data-testid="admin-user-start-input" /></label>
            <label><span className="text-xs font-semibold text-[#5C6B73]">Expiry</span><input type="date" defaultValue={u.expiry_date || ""} onBlur={(e) => e.target.value !== (u.expiry_date || "") && patch({ expiry_date: e.target.value })} className={inp + " mt-1"} data-testid="admin-user-expiry-input" /></label>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {u.status === "suspended"
              ? <button onClick={() => patch({ status: "active" }, "User activated")} className="sb-btn-primary min-h-[40px] text-sm" data-testid="admin-user-activate-btn">Activate</button>
              : <button onClick={() => patch({ status: "suspended" }, "User suspended")} className="sb-btn-ghost min-h-[40px] text-sm text-[#E05A47]" data-testid="admin-user-suspend-btn">Suspend</button>}
            {!["NONE", "FREE"].includes(u.plan) && <button onClick={() => window.confirm("Move this user back to the Free plan?") && patch({ plan: "FREE" }, "User moved to Free plan")} className="sb-btn-ghost min-h-[40px] text-sm" data-testid="admin-user-remove-plan-btn">Remove plan access</button>}
            <button onClick={() => extend(30)} className="sb-btn-ghost min-h-[40px] text-sm" data-testid="admin-user-extend-btn">Extend 30 days</button>
            <button onClick={reset} className="sb-btn-ghost min-h-[40px] text-sm" data-testid="admin-user-reset-btn"><KeyRound className="h-4 w-4" />Reset password link</button>
          </div>
          <div className="text-xs text-[#8C9BA5] mt-4 space-y-0.5">
            <div>Status: <b className="capitalize" data-testid="admin-user-status">{u.status}</b></div>
            <div>Joined: {u.created_at?.slice(0, 10)} · Start: {u.start_date || "—"}</div>
            <div>Last active: {u.last_active ? new Date(u.last_active).toLocaleString() : "never"}</div>
            {u.notes && <div>Notes: {u.notes}</div>}
          </div>
        </section>
        <section className="sb-card p-6" data-testid="admin-user-business">
          <div className="sb-eyebrow">Active business</div>
          {biz ? (<>
            <div className="text-xl font-extrabold font-display mt-1">{biz.name}</div>
            <div className="text-xs text-[#8C9BA5]">{biz.idea_name}</div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div><div className="text-xs text-[#8C9BA5]">Current stage</div><b data-testid="admin-user-stage">{biz.current_stage}</b></div>
              <div><div className="text-xs text-[#8C9BA5]">Progress</div><b data-testid="admin-user-progress">{biz.overall}%</b></div>
              <div><div className="text-xs text-[#8C9BA5]">Lessons</div><b>{biz.lessons}</b></div>
              <div><div className="text-xs text-[#8C9BA5]">Checklist</div><b>{biz.checklist}</b></div>
            </div>
            <ProgressBar value={biz.overall} className="mt-4" />
            {d.businesses.length > 1 && <div className="text-xs text-[#8C9BA5] mt-3">+{d.businesses.length - 1} other business(es)</div>}
          </>) : <p className="text-sm text-[#8C9BA5] mt-2">No business created yet.</p>}
        </section>
      </div>
      <section className="sb-card p-6 mt-4" data-testid="admin-user-permissions">
        <div className="sb-eyebrow mb-3">Permissions</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {d.all_permissions.map((p) => {
            const on = u.permissions.includes(p);
            return (
              <button key={p} onClick={() => togglePerm(p)} data-testid={`admin-perm-${p}`}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold ${on ? "border-[#1DB99A] bg-[#E8F8F4]" : "border-[#EADEC9] text-[#8C9BA5]"}`}>
                {on ? <Check className="h-4 w-4 text-[#1DB99A]" /> : <X className="h-4 w-4" />}{PERM_LABEL[p]}</button>
            );
          })}
        </div>
        <p className="text-xs text-[#8C9BA5] mt-3">Toggling creates a per-user override on top of the plan.</p>
      </section>
      <InviteLink token={token} onClose={() => setToken(null)} kind="reset" />
    </div>
  );
}
